import * as Cesium from 'cesium';

/**
 * Manages two Cesium Cartesian3 points and a width value.
 */
export class pointCloudProfile {
  /**
   * @param {Cesium.Cartesian3} [point1]
   * @param {Cesium.Cartesian3} [point2]
   * @param {number} [width=1]
   */
  constructor(point1, point2, width) {
    this.point1 = point1;
    this.point2 = point2;
    this.width =  width;

    let box = this.computeBox(point1, point2, width);
    this.center = box.center;
    this.dimensions = box.dimensions;
    this.orientation = box.orientation;
  }

  /**
   * Compute an oriented box for this profile.
   */
  computeBox(point1, point2, width) {
    // Calculate profiler box parameters for shader highlighting
    const center = Cesium.Cartesian3.midpoint(point1, point2, new Cesium.Cartesian3());
    const length = Cesium.Cartesian3.distance(point1, point2);
    const height = 1000; // Use a reasonable default height for now
    
    // Position the box at the center point
    const centerCartographic = Cesium.Cartographic.fromCartesian(center);
    const finalPosition = Cesium.Cartesian3.fromRadians(
      centerCartographic.longitude,
      centerCartographic.latitude,
      centerCartographic.height
    );
    
    // Calculate heading to align box with line between points
    const cartographic1 = Cesium.Cartographic.fromCartesian(point1);
    const cartographic2 = Cesium.Cartographic.fromCartesian(point2);
    
    // Calculate proper geodesic bearing using Cesium's EllipsoidGeodesic
    const ellipsoid = Cesium.Ellipsoid.WGS84;
    const geodesic = new Cesium.EllipsoidGeodesic(cartographic1, cartographic2, ellipsoid);
    const bearing = geodesic.startHeading;
    
    // Create orientation using heading/pitch/roll
    const pitch = 0; // No pitch (no up/down rotation)
    const roll = 0;  // No roll (no side rotation)
    const hpr = new Cesium.HeadingPitchRoll(bearing, pitch, roll);
    const orientation = Cesium.Transforms.headingPitchRollQuaternion(finalPosition, hpr);
    
    // Create box dimensions (width, length, height)
    const boxDimensions = new Cesium.Cartesian3(width, length, height);
    
    // Calculate orientation matrix from quaternion
    const orientationMatrix = Cesium.Matrix3.fromQuaternion(orientation);

    return {center:finalPosition,dimensions:boxDimensions,orientation:orientationMatrix}
  }

  updateWidth(width)
  {
    this.width =  width;

    let box = this.computeBox(this.point1, this.point2, width);
    this.center = box.center;
    this.dimensions = box.dimensions;
    this.orientation = box.orientation;
  }

  /**
   * Translate the profile by a world-space delta.
   * @param {Cesium.Cartesian3} delta
   */
  updateOffset(delta) {
    if (!delta) return;
    this.point1 = Cesium.Cartesian3.add(this.point1, delta, new Cesium.Cartesian3());
    this.point2 = Cesium.Cartesian3.add(this.point2, delta, new Cesium.Cartesian3());
    const box = this.computeBox(this.point1, this.point2, this.width);
    this.center = box.center;
    this.dimensions = box.dimensions;
    this.orientation = box.orientation;
  }

  /**
   * Translate one endpoint (point1 or point2) by a world-space delta.
   * @param {('point1'|'point2')} which
   * @param {Cesium.Cartesian3} delta
   */
  updatePointOffset(which, delta) {
    if (!delta || (which !== 'point1' && which !== 'point2')) return;
    if (which === 'point1') {
      this.point1 = Cesium.Cartesian3.add(this.point1, delta, new Cesium.Cartesian3());
    } else {
      this.point2 = Cesium.Cartesian3.add(this.point2, delta, new Cesium.Cartesian3());
    }
    const box = this.computeBox(this.point1, this.point2, this.width);
    this.center = box.center;
    this.dimensions = box.dimensions;
    this.orientation = box.orientation;
  }
}
